/**
 * Deep-linkable cards. A shared link opens the exact card client-side (no server
 * routing needed) via a URL hash like `#card=<id>`. This makes every share, the
 * primary growth loop, land the recipient on the specific story, not the feed top.
 */

/** Extract a card id from a location hash or search string; null if absent. */
export function parseCardLink(hashOrSearch: string): string | null {
  if (!hashOrSearch) return null;
  // Accept "#card=ID", "?card=ID", "card=ID", with optional other params.
  const cleaned = hashOrSearch.replace(/^[#?]/, "");
  for (const part of cleaned.split("&")) {
    const [key, value] = part.split("=");
    if (key === "card" && value) {
      const id = decodeURIComponent(value).trim();
      // Card ids are lowercase slugs; guard against junk.
      return /^[a-z0-9][a-z0-9-]{3,}$/.test(id) ? id : null;
    }
  }
  return null;
}

/** Ordered follow-on story ids from a push link's `next=` chain (comma separated), validated
 * like card ids and capped, so a push can queue "read this, then these" without trusting the
 * URL. Empty array when absent. */
export function parseNextChain(hashOrSearch: string): string[] {
  if (!hashOrSearch) return [];
  const cleaned = hashOrSearch.replace(/^[#?]/, "");
  for (const part of cleaned.split("&")) {
    const [key, value] = part.split("=");
    if (key === "next" && value) {
      return decodeURIComponent(value)
        .split(",")
        .map((id) => id.trim())
        .filter((id) => /^[a-z0-9][a-z0-9-]{3,}$/.test(id))
        .slice(0, 5);
    }
  }
  return [];
}

const CANONICAL_SITE = "https://insnip.com";

/** Build a shareable absolute link to a card, the canonical `/c/<id>` permalink.
 *
 * Unlike a bare `#card=` hash link (which crawlers never see, so it only ever unfurls the
 * generic site preview), the permalink is a real page that serves a per-card rich preview
 * (OpenGraph/Twitter) for link unfurling + SEO, then redirects a human straight into the app
 * on the exact card (`#card=<id>&ref=s`, the "shared with you" conversion step of the loop).
 *
 * A shared link must resolve publicly, so app/preview origins (capacitor://localhost,
 * localhost:5173) fall back to the canonical site. An optional sharer id is carried as `?r=`
 * for referral attribution. */
export function buildCardLink(origin: string, id: string, sharerId?: string): string {
  const isPublicWeb = /^https:\/\//.test(origin) && !/localhost/.test(origin);
  const host = isPublicWeb ? origin.replace(/\/+$/, "") : CANONICAL_SITE;
  const ref = sharerId ? `?r=${encodeURIComponent(sharerId)}` : "";
  return `${host}/c/${encodeURIComponent(id)}${ref}`;
}

/** True if a hash/search string came from a shared link (ref=s). */
export function isSharedLink(hashOrSearch: string): boolean {
  if (!hashOrSearch) return false;
  return /(?:^|[#?&])ref=s(?:&|$)/.test(hashOrSearch.replace(/^[#?]/, "&"));
}

/** The referrer's id from a shared link (the `r` param the permalink passes through), or
 * null. Powers referral attribution, who sent the link that brought a reader in. */
export function parseReferrer(hashOrSearch: string): string | null {
  if (!hashOrSearch) return null;
  const cleaned = hashOrSearch.replace(/^[#?]/, "");
  for (const part of cleaned.split("&")) {
    const [key, value] = part.split("=");
    if (key === "r" && value) {
      const r = decodeURIComponent(value).trim().slice(0, 64);
      return /^[a-z0-9][a-z0-9-]{3,}$/i.test(r) ? r : null;
    }
  }
  return null;
}

/** The story a reader has ALREADY read before arriving, from the share page's `seen=`.
 *
 * `card=` means "lead with this story": it is how a shared link lands someone on the thing
 * that was sent to them. `seen=` is the opposite instruction. The share page now prints the
 * whole story, and its button says "Read the other nine", so the reader who presses it has
 * finished that one and must not be dealt it again as story 01 of the nine. Same id rules as
 * a card link, so junk in the URL cannot name a card. */
export function parseSeenCard(hashOrSearch: string): string | null {
  if (!hashOrSearch) return null;
  const cleaned = hashOrSearch.replace(/^[#?]/, "");
  for (const part of cleaned.split("&")) {
    const [key, value] = part.split("=");
    if (key === "seen" && value) {
      const id = decodeURIComponent(value).trim().toLowerCase();
      return /^[a-z0-9][a-z0-9-]{3,}$/.test(id) ? id : null;
    }
  }
  return null;
}
